import React from "react";
import { useNavigate } from "react-router-dom";
import { Box, Dialog, InputBase, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import SubdirectoryArrowLeftIcon from "@mui/icons-material/SubdirectoryArrowLeft";
import { useAuth } from "../../common/auth";
import { ROUTES } from "../../common/constant/routes";
import { SearchResults, searchAdmin } from "../../common/requests/overview/endpoints";
import { C } from "../../common/theme";
import { formatMoney } from "../../common/utils/format";
import { useIsNarrow } from "../../ui/hooks";
import { NavItem } from "./nav";

interface Hit {
  key: string;
  group: string;
  icon: React.ReactNode;
  title: React.ReactNode;
  sub?: React.ReactNode;
  to: string;
}

const shortId = (id: string) => id.slice(0, 8);
const day = (iso: string) => new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

function toHits(results: SearchResults | null): Hit[] {
  if (!results) return [];
  const hits: Hit[] = [];
  (results.orders ?? []).forEach((o) =>
    hits.push({
      key: `o-${o.order_id}`,
      group: "Orders",
      icon: <ReceiptLongOutlinedIcon />,
      title: `Order #${shortId(o.order_id)}`,
      sub: [o.buyer ? `@${o.buyer}` : null, formatMoney(o.total, o.currency), day(o.created_at)].filter(Boolean).join(" · "),
      to: `${ROUTES.SALES}?order=${encodeURIComponent(o.order_id)}`,
    })
  );
  (results.users ?? []).forEach((u) =>
    hits.push({
      key: `u-${u.user_id}`,
      group: "Users & sellers",
      icon: u.is_seller ? <StorefrontOutlinedIcon /> : <PersonOutlineIcon />,
      title: u.username ? `@${u.username}` : u.email,
      sub: [u.is_seller ? "Seller" : "Buyer", u.email, u.country].filter(Boolean).join(" · "),
      to: `${ROUTES.USER_MANAGEMENT}?tab=users&user=${encodeURIComponent(u.user_id)}`,
    })
  );
  (results.payouts ?? []).forEach((p) =>
    hits.push({
      key: `p-${p.withdrawal_id}`,
      group: "Payouts",
      icon: <AccountBalanceWalletOutlinedIcon />,
      title: `${p.username ? `@${p.username}` : "Payout"} · ${formatMoney(p.amount, p.currency)}`,
      sub: `${p.status} · requested ${day(p.request_date)}`,
      to: `${ROUTES.WITHDRAWALS}?view=all&payout=${encodeURIComponent(p.withdrawal_id)}`,
    })
  );
  return hits;
}

/** ⌘K / Ctrl+K: jump to a page, or find an order, user or payout and open it. */
export default function CommandPalette({ open, onClose, pages }: { open: boolean; onClose: () => void; pages: NavItem[] }) {
  const { idToken } = useAuth();
  const navigate = useNavigate();
  const narrow = useIsNarrow(600);
  const [q, setQ] = React.useState("");
  const [results, setResults] = React.useState<SearchResults | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [failed, setFailed] = React.useState(false);
  const [active, setActive] = React.useState(0);
  const listRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (open) {
      setQ("");
      setResults(null);
      setActive(0);
      setFailed(false);
    }
  }, [open]);

  React.useEffect(() => {
    const term = q.trim();
    if (term.length < 2 || !idToken) {
      setResults(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    let alive = true;
    const id = window.setTimeout(async () => {
      try {
        const { data } = await searchAdmin(idToken, term);
        if (alive) {
          setResults(data);
          setFailed(false);
        }
      } catch {
        if (alive) setFailed(true);
      } finally {
        if (alive) setLoading(false);
      }
    }, 220);
    return () => {
      alive = false;
      window.clearTimeout(id);
    };
  }, [q, idToken]);

  const term = q.trim().toLowerCase();
  const pageHits: Hit[] = pages
    .filter((p) => !term || p.label.toLowerCase().includes(term))
    .map((p) => ({ key: `page-${p.path}`, group: "Go to", icon: p.icon, title: p.label, to: p.path }));
  const hits = [...(term.length >= 2 ? toHits(results) : []), ...pageHits];

  React.useEffect(() => {
    setActive(0);
  }, [q, results]);
  React.useEffect(() => {
    listRef.current?.querySelector<HTMLElement>(`[data-index="${active}"]`)?.scrollIntoView({ block: "nearest" });
  }, [active]);

  const go = (hit: Hit) => {
    onClose();
    navigate(hit.to);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, hits.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && hits[active]) {
      e.preventDefault();
      go(hits[active]);
    }
  };

  let lastGroup = "";
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={narrow}
      maxWidth={false}
      PaperProps={{ sx: { width: narrow ? "100%" : 620, maxWidth: "calc(100vw - 32px)", alignSelf: "flex-start", mt: narrow ? 0 : "12vh", overflow: "hidden" } }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, px: 2, py: 1.25, pt: narrow ? "calc(10px + env(safe-area-inset-top, 0px))" : 1.25, borderBottom: `1px solid ${C.line}` }}>
        <SearchIcon sx={{ color: C.muted }} />
        <InputBase
          autoFocus
          fullWidth
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Search orders, users, sellers, payouts…"
          inputProps={{ "aria-label": "Search", role: "combobox", "aria-expanded": true, "aria-controls": "cmdk-list" }}
          sx={{ fontSize: 16, minHeight: 44 }}
        />
        <Box component="button" type="button" onClick={onClose} sx={{ all: "unset", cursor: "pointer", fontSize: 12, fontWeight: 700, color: C.muted, border: `1px solid ${C.line2}`, borderRadius: "5px", px: 0.75, py: 0.25 }}>
          {narrow ? "Close" : "Esc"}
        </Box>
      </Box>
      <Box id="cmdk-list" role="listbox" ref={listRef} sx={{ maxHeight: narrow ? "none" : 420, flex: narrow ? 1 : undefined, overflowY: "auto", py: 1 }}>
        {term.length >= 2 && loading && !results && (
          <Typography variant="body2" sx={{ px: 2, py: 1.5, color: C.muted }}>
            Searching…
          </Typography>
        )}
        {term.length >= 2 && failed && (
          <Typography variant="body2" sx={{ px: 2, py: 1.5, color: C.danger }}>
            Search didn't work. Try again.
          </Typography>
        )}
        {term.length >= 2 && results && !loading && hits.length === pageHits.length && (
          <Typography variant="body2" sx={{ px: 2, py: 1.5, color: C.muted }}>
            No orders, users or payouts match “{q.trim()}”.
          </Typography>
        )}
        {hits.map((hit, index) => {
          const header = hit.group !== lastGroup ? hit.group : null;
          lastGroup = hit.group;
          const selected = index === active;
          return (
            <React.Fragment key={hit.key}>
              {header && (
                <Typography variant="overline" component="div" sx={{ px: 2, pt: index ? 1.25 : 0.5, pb: 0.25, color: C.muted }}>
                  {header}
                </Typography>
              )}
              <Box
                role="option"
                aria-selected={selected}
                data-index={index}
                onMouseEnter={() => setActive(index)}
                onClick={() => go(hit)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  mx: 1,
                  px: 1.25,
                  minHeight: 48,
                  borderRadius: "6px",
                  cursor: "pointer",
                  bgcolor: selected ? C.bone1 : "transparent",
                  "& > svg, & > .ic svg": { fontSize: 20 },
                }}
              >
                <Box className="ic" sx={{ color: C.ink2, display: "flex" }}>
                  {hit.icon}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                    {hit.title}
                  </Typography>
                  {hit.sub && (
                    <Typography variant="caption" sx={{ color: C.muted, display: "block" }} noWrap>
                      {hit.sub}
                    </Typography>
                  )}
                </Box>
                {selected && !narrow && <SubdirectoryArrowLeftIcon sx={{ fontSize: 16, color: C.muted }} />}
              </Box>
            </React.Fragment>
          );
        })}
      </Box>
    </Dialog>
  );
}
